import Button from '@atlaskit/button/standard-button';
import React from 'react';
import { useSelector } from 'react-redux';
import ModalDialog, {
  ModalBody,
  ModalTitle,
  ModalHeader,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import { FormattedMessage } from 'react-intl';
import { messages } from 'modules/mac/messages';
import t from 'modules/i18n/intl';
import {
  getCurrentMACContactEmail,
  selectMakeContactPrimaryProgress,
} from 'modules/mac/duck/selectors';
import { LoadingButton } from '@atlaskit/button';

type PropTypes = {
  onCancel: () => void;
  onConfirm: () => void;
  currentEmail: string;
  currentMainEmail: string;
  kind: 'technical' | 'billing';
  accountId: string;
  extraMessage?: React.ReactNode;
};

export const PromotionContent: React.FC<PropTypes> = ({
  onCancel,
  onConfirm,
  currentEmail,
  currentMainEmail,
  kind,
  accountId,
  extraMessage,
}) => {
  const currentMACUserEmail = useSelector(getCurrentMACContactEmail);
  const makeContactPrimaryProgress = useSelector(selectMakeContactPrimaryProgress);

  const promotingYourself = currentMACUserEmail === currentEmail;

  return (
    <ModalDialog
      autoFocus
      onClose={onCancel}
      shouldCloseOnOverlayClick
      shouldCloseOnEscapePress
      width="medium"
    >
      <ModalHeader>
        <ModalTitle>
          <FormattedMessage
            {...messages.promotingTitle}
            values={{
              // A space after 'yourself' is needed
              whom: promotingYourself ? t('mac.licenses.make.primary.yourself') : '',
              kind: t(`mac.licenses.${kind}.promotion`),
            }}
          />
        </ModalTitle>
      </ModalHeader>
      <ModalBody data-cy="make-primary-modal">
        <p>
          {promotingYourself ? (
            <FormattedMessage
              {...messages.promoteYourself}
              values={{
                email: <b>{currentMainEmail}</b>,
                kind: t(`mac.licenses.${kind}.promotion`),
              }}
            />
          ) : (
            <FormattedMessage
              {...messages.promoteOther}
              values={{
                email: <b>{currentEmail}</b>,
                kind: t(`mac.licenses.${kind}.promotion`),
                accountId: `SEN-${accountId}`,
              }}
            />
          )}
        </p>
        <p>
          <FormattedMessage
            {...messages.promotionAbility}
            values={{
              person: promotingYourself
                ? t('mac.licenses.make.primary.you')
                : t('mac.licenses.make.primary.they'),
            }}
          />
        </p>
        <ul>
          <li>
            <FormattedMessage
              {...(promotingYourself
                ? messages.selfPromotionPrivilege1
                : messages.promotionPrivilege1)}
            />
          </li>
          <li>
            <FormattedMessage
              {...(promotingYourself
                ? messages.selfPromotionPrivilege2
                : messages.promotionPrivilege2)}
            />
          </li>
        </ul>
        <p>
          {promotingYourself ? (
            <FormattedMessage
              {...messages.promotionLose}
              values={{
                email: currentMainEmail,
                kind: t(`mac.licenses.${kind}.promotion`),
              }}
            />
          ) : (
            <FormattedMessage {...messages.promotionPastInvoices} />
          )}
        </p>
        <p>
          <FormattedMessage
            {...messages.promotionNotification}
            values={{
              email: currentMainEmail,
            }}
          />
        </p>
        <br />
        {extraMessage}
      </ModalBody>
      <ModalFooter>
        <LoadingButton
          data-cy="make-primary-modal-confirm"
          appearance="primary"
          onClick={onConfirm}
          isLoading={makeContactPrimaryProgress?.inProgress}
        >
          <FormattedMessage {...messages.promotionConfirm} />
        </LoadingButton>
        <Button data-cy="make-primary-modal-cancel" appearance="default" onClick={onCancel}>
          <FormattedMessage {...messages.promotionCancel} />
        </Button>
      </ModalFooter>
    </ModalDialog>
  );
};
